// WebConnectorPage - Gestion des connecteurs web (formulaires, webhooks)
import { useState, useEffect } from "react";
import { Globe, Webhook, FileJson, Plus, Trash2, Copy, ExternalLink, RefreshCw, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WebConnectorPage() {
  const [webhooks, setWebhooks] = useState([
    { id: "wh-1", name: "Formulaire Contact", url: `${API}/api/webhooks/contact`, type: "form", status: "active", calls: 156 },
    { id: "wh-2", name: "Webhook Calendly", url: `${API}/api/webhooks/calendly`, type: "webhook", status: "active", calls: 42 },
    { id: "wh-3", name: "API Leads", url: `${API}/api/webhooks/leads`, type: "api", status: "inactive", calls: 0 }
  ]);
  const [copied, setCopied] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success("URL copiée");
    setTimeout(() => setCopied(null), 2000);
  };

  const typeColors = {
    form: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    webhook: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    api: "bg-green-500/20 text-green-400 border-green-500/30"
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
            <Globe className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Web Connector</h1>
            <p className="text-sm text-zinc-400">Gérez vos points d'entrée web (formulaires, webhooks, API)</p>
          </div>
        </div>
        <Button className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau connecteur
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{webhooks.length}</p>
                <p className="text-sm text-zinc-400">Connecteurs</p>
              </div>
              <Webhook className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{webhooks.filter(w => w.status === 'active').length}</p>
                <p className="text-sm text-zinc-400">Actifs</p>
              </div>
              <Check className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{webhooks.reduce((acc, w) => acc + w.calls, 0)}</p>
                <p className="text-sm text-zinc-400">Appels totaux</p>
              </div>
              <RefreshCw className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg">Connecteurs configurés</CardTitle>
          <CardDescription>Points d'entrée pour la réception de données externes</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-zinc-400">Nom</TableHead>
                <TableHead className="text-zinc-400">Type</TableHead>
                <TableHead className="text-zinc-400">URL</TableHead>
                <TableHead className="text-zinc-400">Statut</TableHead>
                <TableHead className="text-zinc-400">Appels</TableHead>
                <TableHead className="text-zinc-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhooks.map(webhook => (
                <TableRow key={webhook.id} className="border-zinc-800">
                  <TableCell className="font-medium text-white">{webhook.name}</TableCell>
                  <TableCell>
                    <Badge className={typeColors[webhook.type]}>
                      {webhook.type.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-zinc-400 max-w-[200px] truncate">
                    {webhook.url}
                  </TableCell>
                  <TableCell>
                    <Badge className={webhook.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}>
                      {webhook.status === 'active' ? 'Actif' : 'Inactif'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-zinc-300">{webhook.calls}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(webhook.url, webhook.id)}
                      >
                        {copied === webhook.id ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
